import React, { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  CalendarClock,
  LineChart as IconeCourbe,
  ListChecks,
  PieChart,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import { OnboardingChecklist } from '../../components/gamification';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Loader from '../../components/Common/Loader';
import {
  CotationPalmares,
  DonneesDashboard,
  EvenementAVenir,
  GraphiquesPortefeuille,
  Nouveaute,
  Repartition,
  TitreDuJour,
  useDashboard,
} from '../../hooks/useDashboard';
import './Dashboard.css';

/*
 * Tableau de bord personnel. Fondement : docs/tableau_de_bord/ (analyse et décisions du
 * 22-23/10/2026). L'écran reprenait surtout la page 1 du BOC, publiée gratuitement par la
 * BRVM ; il montre d'abord ce qui concerne l'utilisateur, puis le marché en une ligne.
 *
 * Volontairement absents (littérature citée dans le document) : classement des
 * portefeuilles, palmarès de volume « anormal », toute alerte de variation de cours.
 * Couleurs : une par sens (hausse, baisse, type d'événement, bloc), contrastes vérifiés,
 * jamais seules (signe, flèche ou libellé) — section « Couleurs » du document.
 */

const IndicesChart = React.lazy(() => import('./IndicesChart'));
const PerformanceChart = React.lazy(() => import('./PerformanceChart'));

const NB_LIGNES_REPLIEES = 6;

// ---------- formats (français, montants entiers) ----------

const pct = (v: number | null | undefined): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const texte = v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v > 0 ? '+' : ''}${texte} %`;
};

const fcfa = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : `${Math.round(v).toLocaleString('fr-FR')} FCFA`;

const montantCourt = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '–';
  if (v >= 1e9) return `${(v / 1e9).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Md FCFA`;
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M FCFA`;
  return fcfa(v);
};

const dateIso = (iso: string): Date => {
  const [a, m, j] = iso.slice(0, 10).split('-').map(Number);
  return new Date(a, m - 1, j);
};

const dateCourte = (iso: string): string =>
  dateIso(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const classeSigne = (v: number | null | undefined): string =>
  v === null || v === undefined || v === 0 ? '' : v > 0 ? 'dash-up' : 'dash-down';

/** Variation colorée ET fléchée : la couleur n'est jamais le seul indice (WCAG 1.4.1). */
const Variation: React.FC<{ v: number | null | undefined; pastille?: boolean }> = ({
  v,
  pastille,
}) => (
  <span className={`${classeSigne(v)}${pastille ? ' dash-pastille' : ''}`}>
    {v !== null && v !== undefined && v !== 0 && (
      <span className='dash-fleche' aria-hidden='true'>
        {v > 0 ? '▲' : '▼'}
      </span>
    )}
    <span>{pct(v)}</span>
  </span>
);

type IconeLucide = React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;

/** Titre de bloc avec son icône, dans la couleur d'accent du bloc. */
const TitreBloc: React.FC<{ icone: IconeLucide; children: React.ReactNode }> = ({
  icone: Icone,
  children,
}) => (
  <span>
    <span className='dash-icone'>
      <Icone size={17} aria-hidden={true} />
    </span>
    {children}
  </span>
);

// ---------- 0. Depuis votre dernière visite ----------

const TYPE_NOUVEAUTE: Record<Nouveaute['type'], string> = {
  RAPPORT: 'publication',
  AVIS: 'nouveaute',
  DIVIDENDE_ANNONCE: 'dividende',
  DIVIDENDE_CREDITE: 'dividende',
};

const LIBELLE_NOUVEAUTE: Record<Nouveaute['type'], string> = {
  RAPPORT: 'Rapport',
  AVIS: 'Avis BRVM',
  DIVIDENDE_ANNONCE: 'Dividende annoncé',
  DIVIDENDE_CREDITE: 'Dividende crédité',
};

const DepuisDerniereVisite: React.FC<{ elements: Nouveaute[]; depuis: string | null }> = ({
  elements,
  depuis,
}) => {
  const [tout, setTout] = useState(false);
  const visibles = tout ? elements : elements.slice(0, 5);
  return (
    <section
      className='dash-card dash-news'
      data-accent='nouveaute'
      aria-labelledby='dash-news-titre'
    >
      <h2 className='dash-card-title' id='dash-news-titre'>
        <TitreBloc icone={Sparkles}>
          Depuis votre dernière visite{depuis ? ` (${dateCourte(depuis)})` : ''}
        </TitreBloc>
      </h2>
      {elements.length === 0 ? (
        <p className='dash-muted' style={{ margin: 0 }}>
          Rien de nouveau sur vos titres{depuis ? ` depuis le ${dateCourte(depuis)}` : ''}.
        </p>
      ) : (
        <>
          <ul>
            {visibles.map((e, i) => (
              <li key={`${e.type}-${e.symbole}-${e.date}-${i}`}>
                <span className='dash-date'>{dateCourte(e.date)}</span>
                <span>
                  {e.symbole && (
                    <Link className='dash-symbol' to={`/stock/${e.symbole}`}>
                      {e.symbole}
                    </Link>
                  )}{' '}
                  <span className='dash-badge' data-type={TYPE_NOUVEAUTE[e.type]}>
                    {LIBELLE_NOUVEAUTE[e.type]}
                  </span>{' '}
                  {e.type === 'DIVIDENDE_CREDITE' && e.montant !== undefined ? (
                    `${fcfa(e.montant)} crédités sur votre portefeuille`
                  ) : e.url ? (
                    <a className='dash-link' href={e.url} target='_blank' rel='noopener noreferrer'>
                      {e.libelle}
                    </a>
                  ) : (
                    e.libelle
                  )}
                </span>
              </li>
            ))}
          </ul>
          {elements.length > 5 && (
            <button
              type='button'
              className='dash-tab'
              style={{ marginTop: 8 }}
              onClick={() => setTout(!tout)}
            >
              {tout ? 'Réduire' : `Voir les ${elements.length} nouveautés`}
            </button>
          )}
        </>
      )}
    </section>
  );
};

// ---------- 1. Mon portefeuille, sur la durée ----------

const CartePortefeuille: React.FC<{ d: DonneesDashboard }> = ({ d }) => {
  const p = d.portefeuille;
  const composite = d.marche?.indices.find((i) => i.nom === 'BRVM COMPOSITE');
  const aDesTitres = !!p && p.valeur_actuelle > 0;
  return (
    <section className='dash-card dash-hero' aria-labelledby='dash-pf-titre'>
      <h2 className='dash-card-title' id='dash-pf-titre'>
        <TitreBloc icone={Wallet}>Mon portefeuille</TitreBloc>
        <Link to='/my-assets'>Détails →</Link>
      </h2>
      {!aDesTitres ? (
        <p className='dash-muted' style={{ margin: 0 }}>
          Ajoutez vos titres dans{' '}
          <Link className='dash-link' to='/my-assets'>
            Mes actifs
          </Link>{' '}
          pour suivre leur performance, leurs dividendes et leurs échéances ici.
        </p>
      ) : (
        <>
          <div className='dash-kpi-label'>Valeur totale (titres et liquidités)</div>
          <div className='dash-total'>
            {Math.round(p.valeur_actuelle + d.liquidites).toLocaleString('fr-FR')}{' '}
            <small>FCFA</small>
          </div>
          <div className='dash-perf'>
            <div>
              <div className='dash-kpi-label'>Depuis l’origine</div>
              <div className='dash-kpi-value'>
                <Variation v={p.performance_total} pastille />
              </div>
            </div>
            <div>
              <div className='dash-kpi-label'>Depuis janvier</div>
              <div className='dash-kpi-value'>
                <Variation v={p.performance_annuel} pastille />
              </div>
              {composite && (
                <div className='dash-kpi-sub'>Composite : {pct(composite.variation_annee_pct)}</div>
              )}
            </div>
            {d.dividendes && (
              <div>
                <div className='dash-kpi-label'>Dividendes {d.dividendes.annee}</div>
                <div className='dash-kpi-value'>{fcfa(d.dividendes.encaisse)}</div>
                {d.dividendes.annonce_a_venir > 0 && (
                  <div className='dash-kpi-sub'>
                    + {fcfa(d.dividendes.annonce_a_venir)} annoncés
                  </div>
                )}
              </div>
            )}
            <div>
              <div className='dash-kpi-label'>Dernière séance</div>
              <div className='dash-kpi-sub' style={{ fontSize: 14 }}>
                <Variation v={p.day_pl_pct} pastille /> {p.day_pl > 0 ? '+' : ''}
                {fcfa(p.day_pl)}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

// ---------- 2. À venir sur mes titres ----------

const detailEvenement = (e: EvenementAVenir): string | null => {
  if (e.type === 'PAIEMENT_DIVIDENDE' && e.montant_net_position) {
    return `environ ${fcfa(e.montant_net_position)} nets pour votre position`;
  }
  if ((e.type === 'DETACHEMENT' || e.type === 'PAIEMENT_DIVIDENDE') && e.montant_brut) {
    const brut = `${e.montant_brut.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} F brut par action`;
    return e.rendement_brut_pct
      ? `${brut}, soit ${pct(e.rendement_brut_pct).replace('+', '')} du cours`
      : brut;
  }
  if (e.type === 'AG' && e.lieu) return e.lieu;
  return null;
};

const TYPE_EVENEMENT: Record<EvenementAVenir['type'], [string, string]> = {
  DETACHEMENT: ['dividende', 'Détachement'],
  PAIEMENT_DIVIDENDE: ['dividende', 'Paiement'],
  FRACTIONNEMENT: ['operation', 'Opération'],
  AUGMENTATION_CAPITAL: ['operation', 'Opération'],
  AG: ['ag', 'AG'],
  PUBLICATION: ['publication', 'Publication'],
  MARCHE_FERME: ['marche', 'Marché'],
};

const AVenir: React.FC<{ evenements: EvenementAVenir[]; suitDesTitres: boolean }> = ({
  evenements,
  suitDesTitres,
}) => {
  const [tout, setTout] = useState(false);
  const visibles = tout ? evenements : evenements.slice(0, NB_LIGNES_REPLIEES);
  return (
    <section className='dash-card' data-accent='avenir' aria-labelledby='dash-avenir-titre'>
      <h2 className='dash-card-title' id='dash-avenir-titre'>
        <TitreBloc icone={CalendarClock}>À venir sur mes titres · 30 jours</TitreBloc>
        <Link to='/calendar'>Calendrier →</Link>
      </h2>
      {!suitDesTitres ? (
        <p className='dash-muted' style={{ margin: 0 }}>
          Ajoutez des titres à vos{' '}
          <Link className='dash-link' to='/favorites'>
            favoris
          </Link>{' '}
          ou à votre portefeuille : leurs dividendes, assemblées générales et publications attendues
          apparaîtront ici.
        </p>
      ) : evenements.length === 0 ? (
        <p className='dash-muted' style={{ margin: 0 }}>
          Aucun événement daté dans les 30 prochains jours sur vos titres.
        </p>
      ) : (
        <>
          <ul className='dash-events'>
            {visibles.map((e, i) => {
              const detail = detailEvenement(e);
              return (
                <li key={`${e.type}-${e.symbole}-${e.date}-${i}`}>
                  <span className='dash-date'>{dateCourte(e.date)}</span>
                  <span>
                    <span
                      className='dash-badge'
                      data-type={TYPE_EVENEMENT[e.type][0]}
                      style={{ marginLeft: 0, marginRight: 6 }}
                    >
                      {TYPE_EVENEMENT[e.type][1]}
                    </span>
                    {e.symbole && (
                      <Link className='dash-symbol' to={`/stock/${e.symbole}`}>
                        {e.symbole}
                      </Link>
                    )}{' '}
                    {e.url ? (
                      <a
                        className='dash-link'
                        href={e.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {e.libelle}
                      </a>
                    ) : (
                      e.libelle
                    )}
                    {e.certitude === 'ESTIME' && (
                      <span
                        className='dash-badge'
                        title='Date estimée d’après les délais de publication des exercices précédents'
                      >
                        estimée{e.fiabilite ? ` · fiabilité ${e.fiabilite.toLowerCase()}` : ''}
                      </span>
                    )}
                    {e.symbole && !e.detenu && <span className='dash-badge'>favori</span>}
                    {detail && <div className='dash-kpi-sub'>{detail}</div>}
                  </span>
                </li>
              );
            })}
          </ul>
          {evenements.length > NB_LIGNES_REPLIEES && (
            <button
              type='button'
              className='dash-tab'
              style={{ marginTop: 8 }}
              onClick={() => setTout(!tout)}
            >
              {tout ? 'Réduire' : `Voir les ${evenements.length} événements`}
            </button>
          )}
        </>
      )}
    </section>
  );
};

// ---------- 3. Ce qui a bougé chez moi, et pourquoi ----------

const MesTitres: React.FC<{ lignes: TitreDuJour[]; date: string | null }> = ({ lignes, date }) => {
  const [tout, setTout] = useState(false);
  if (lignes.length === 0) return null;
  const visibles = tout ? lignes : lignes.slice(0, NB_LIGNES_REPLIEES);
  return (
    <section className='dash-card' data-accent='titres' aria-labelledby='dash-mestitres-titre'>
      <h2 className='dash-card-title' id='dash-mestitres-titre'>
        <TitreBloc icone={ListChecks}>
          Mes titres{date ? ` · séance du ${dateCourte(date)}` : ''}
        </TitreBloc>
        <Link to='/favorites'>Favoris →</Link>
      </h2>
      <div className='dash-table-wrap'>
        <table className='dash-table'>
          <thead>
            <tr>
              <th scope='col'>Titre</th>
              <th scope='col' className='dash-col-secondaire'>
                Cours
              </th>
              <th scope='col'>Jour</th>
              <th scope='col'>Depuis janv.</th>
              <th scope='col' className='dash-col-secondaire'>
                Échangé
              </th>
              <th scope='col' className='dash-col-secondaire' style={{ textAlign: 'left' }}>
                Pourquoi
              </th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((l) => (
              <tr key={l.symbole}>
                <td>
                  <Link className='dash-symbol' to={`/stock/${l.symbole}`}>
                    {l.symbole}
                  </Link>
                  {!l.detenu && <span className='dash-badge'>favori</span>}
                </td>
                <td className='dash-col-secondaire'>
                  {l.cours_cloture?.toLocaleString('fr-FR') ?? '–'}
                </td>
                <td>
                  <Variation v={l.variation_jour_pct} />
                  {l.sans_echange && (
                    <span
                      className='dash-badge'
                      title='Aucun titre échangé : ce n’est pas un prix traité'
                    >
                      sans échange
                    </span>
                  )}
                </td>
                <td>
                  <span className='dash-spark'>
                    <Sparkline valeurs={l.serie_annee} libelle={`${l.symbole} depuis janvier`} />
                    <Variation v={l.variation_annee_pct} />
                  </span>
                </td>
                <td className='dash-col-secondaire'>
                  {l.sans_echange ? '–' : montantCourt(l.valeur_echange)}
                </td>
                <td className='dash-why dash-col-secondaire'>
                  {l.pourquoi ? (
                    l.pourquoi.url ? (
                      <a
                        className='dash-link'
                        href={l.pourquoi.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {l.pourquoi.libelle}
                      </a>
                    ) : (
                      l.pourquoi.libelle
                    )
                  ) : (
                    <span className='dash-muted'>–</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lignes.length > NB_LIGNES_REPLIEES && (
        <button
          type='button'
          className='dash-tab'
          style={{ marginTop: 8 }}
          onClick={() => setTout(!tout)}
        >
          {tout ? 'Réduire' : `Voir mes ${lignes.length} titres`}
        </button>
      )}
    </section>
  );
};

// ---------- graphiques compacts (docs/tableau_de_bord/, « Jauges et graphiques ») ----------

/** Sparkline (Tufte) : un graphique de la taille d'un mot, à côté du chiffre. Neutre en couleur. */
const Sparkline: React.FC<{ valeurs: number[]; libelle: string }> = ({ valeurs, libelle }) => {
  if (valeurs.length < 2) return null;
  const largeur = 72;
  const hauteur = 22;
  const min = Math.min(...valeurs);
  const max = Math.max(...valeurs);
  const etendue = max - min || 1;
  const points = valeurs
    .map(
      (v, i) =>
        `${((i / (valeurs.length - 1)) * largeur).toFixed(1)},${(hauteur - 2 - ((v - min) / etendue) * (hauteur - 4)).toFixed(1)}`
    )
    .join(' ');
  const [dx, dy] = points.split(' ').pop()!.split(',');
  const sens = valeurs[valeurs.length - 1] - valeurs[0];
  const couleur =
    sens > 0 ? 'var(--dash-hausse)' : sens < 0 ? 'var(--dash-baisse)' : 'var(--text-muted)';
  return (
    <svg
      width={largeur}
      height={hauteur}
      viewBox={`0 0 ${largeur} ${hauteur}`}
      role='img'
      aria-label={libelle}
    >
      <polyline points={points} fill='none' stroke={couleur} strokeWidth={1.5} />
      <circle cx={dx} cy={dy} r={2.2} fill={couleur} />
    </svg>
  );
};

/** Hausses / inchangés / baisses en longueurs sur une même barre (Cleveland et McGill). */
const BarreHaussesBaisses: React.FC<{ hausses: number; inchanges: number; baisses: number }> = ({
  hausses,
  inchanges,
  baisses,
}) => {
  const total = hausses + inchanges + baisses;
  if (total === 0) return null;
  const part = (n: number) => `${(n / total) * 100}%`;
  return (
    <div style={{ marginTop: 12 }}>
      <div
        className='dash-breadth'
        role='img'
        aria-label={`${hausses} hausses, ${inchanges} inchangés, ${baisses} baisses`}
      >
        <span style={{ width: part(hausses), background: 'var(--dash-hausse)' }} />
        <span style={{ width: part(inchanges), background: '#94a3b8' }} />
        <span style={{ width: part(baisses), background: 'var(--dash-baisse)' }} />
      </div>
      <div className='dash-breadth-legende'>
        <span>
          <strong>{hausses}</strong> hausses
        </span>
        <span>
          <strong>{inchanges}</strong> inchangés
        </span>
        <span>
          <strong>{baisses}</strong> baisses
        </span>
      </div>
    </div>
  );
};

/** Répartition en barres horizontales (longueur), plutôt qu'en camembert (angle et aire). */
const BarresRepartition: React.FC<{ lignes: Repartition[] }> = ({ lignes }) => (
  <ul className='dash-rows'>
    {lignes.map((l) => (
      <li key={l.label} className='dash-alloc'>
        <span className='dash-alloc-label'>{l.label}</span>
        <span className='dash-alloc-barre'>
          <span style={{ width: `${Math.max(l.percentage, 1)}%` }} />
        </span>
        <span className='dash-alloc-pct'>
          {l.percentage.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %
        </span>
      </li>
    ))}
  </ul>
);

const CartePerformance: React.FC<{ g: GraphiquesPortefeuille }> = ({ g }) => {
  const { courbe } = g;
  if (courbe.portefeuille.length < 2 || !courbe.depuis) return null;
  return (
    <section className='dash-card' data-accent='perf' aria-labelledby='dash-perf-titre'>
      <h2 className='dash-card-title' id='dash-perf-titre'>
        <TitreBloc icone={IconeCourbe}>
          Mon portefeuille et le Composite · depuis le {dateCourte(courbe.depuis)}
        </TitreBloc>
      </h2>
      <div className='dash-perf-fin'>
        <span>
          <span className='dash-pastille-serie' style={{ background: '#6366f1' }} /> Mon
          portefeuille <Variation v={courbe.portefeuille[courbe.portefeuille.length - 1].pct} />
        </span>
        {courbe.composite.length > 0 && (
          <span>
            <span className='dash-pastille-serie' style={{ background: '#94a3b8' }} /> Composite{' '}
            <Variation v={courbe.composite[courbe.composite.length - 1].pct} />
          </span>
        )}
      </div>
      <Suspense fallback={<p className='dash-muted'>Chargement du graphique...</p>}>
        <PerformanceChart portefeuille={courbe.portefeuille} composite={courbe.composite} />
      </Suspense>
      <p className='dash-muted' style={{ margin: '8px 0 0' }}>
        Rendement cumulé hors apports : vos achats et ventes ne comptent pas comme une performance.
        Dividendes non inclus.
      </p>
    </section>
  );
};

const CarteRepartition: React.FC<{ g: GraphiquesPortefeuille }> = ({ g }) => {
  const [axe, setAxe] = useState<'secteurs' | 'pays'>('secteurs');
  if (g.secteurs.length === 0 && g.pays.length === 0) return null;
  return (
    <section className='dash-card' data-accent='repartition' aria-labelledby='dash-repart-titre'>
      <h2 className='dash-card-title' id='dash-repart-titre'>
        <TitreBloc icone={PieChart}>Répartition de mon portefeuille</TitreBloc>
        <Link to='/portfolio-analysis'>Analyse →</Link>
      </h2>
      <div className='dash-tabs' role='tablist' aria-label='Répartition'>
        {(['secteurs', 'pays'] as const).map((a) => (
          <button
            key={a}
            type='button'
            role='tab'
            className='dash-tab'
            aria-selected={axe === a}
            onClick={() => setAxe(a)}
          >
            {a === 'secteurs' ? 'Par secteur' : 'Par pays'}
          </button>
        ))}
      </div>
      <BarresRepartition lignes={g[axe]} />
    </section>
  );
};

// ---------- 4. Le marché en une ligne ----------

const LigneMarche: React.FC<{ d: DonneesDashboard }> = ({ d }) => {
  const m = d.marche;
  if (!m) return null;
  return (
    <section className='dash-card' data-accent='marche' aria-labelledby='dash-marche-titre'>
      <h2 className='dash-card-title' id='dash-marche-titre'>
        <TitreBloc icone={Activity}>
          Marché{m.date ? ` · séance du ${dateCourte(m.date)}` : ''}
        </TitreBloc>
        <Link to='/cotations'>Toutes les actions →</Link>
      </h2>
      <div className='dash-market'>
        {m.indices.map((i) => (
          <span key={i.nom}>
            <strong>
              {i.nom.replace('BRVM COMPOSITE', 'Composite').replace('BRVM PRESTIGE', 'Prestige')}
            </strong>{' '}
            {i.valeur?.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}{' '}
            <Variation v={i.variation_jour_pct} />{' '}
            <span className='dash-muted'>
              · <Variation v={i.variation_annee_pct} /> depuis janvier
            </span>
          </span>
        ))}
        <span>
          Valeur échangée <strong>{montantCourt(m.valeur_echangee)}</strong>
        </span>
      </div>
      {m.nb_hausse !== null && m.nb_baisse !== null && m.nb_inchange !== null && (
        <BarreHaussesBaisses
          hausses={m.nb_hausse}
          inchanges={m.nb_inchange}
          baisses={m.nb_baisse}
        />
      )}
    </section>
  );
};

// ---------- 5. Palmarès de la séance (replié) ----------

type OngletPalmares = 'gainers' | 'losers' | 'plus_echanges';

const ONGLETS: { cle: OngletPalmares; libelle: string; ton: string }[] = [
  { cle: 'gainers', libelle: 'Hausses', ton: 'hausse' },
  { cle: 'losers', libelle: 'Baisses', ton: 'baisse' },
  { cle: 'plus_echanges', libelle: 'Plus échangés', ton: 'volume' },
];

const Palmares: React.FC<{ d: DonneesDashboard }> = ({ d }) => {
  const [onglet, setOnglet] = useState<OngletPalmares>('gainers');
  const lignes: CotationPalmares[] = d.palmares[onglet];
  return (
    <details className='dash-card dash-fold' data-accent='palmares'>
      <summary>
        <TitreBloc icone={BarChart3}>Palmarès de la séance ▾</TitreBloc>
      </summary>
      <div className='dash-tabs' role='tablist' aria-label='Palmarès de la séance'>
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type='button'
            role='tab'
            className='dash-tab'
            data-ton={o.ton}
            aria-selected={onglet === o.cle}
            onClick={() => setOnglet(o.cle)}
          >
            {o.libelle}
          </button>
        ))}
      </div>
      {lignes.length === 0 ? (
        <p className='dash-muted'>Aucun titre dans cette catégorie à la dernière séance.</p>
      ) : (
        <div className='dash-table-wrap'>
          <table className='dash-table'>
            <thead>
              <tr>
                <th scope='col'>Titre</th>
                <th scope='col'>Cours</th>
                <th scope='col'>Jour</th>
                <th scope='col'>Depuis janv.</th>
                <th scope='col' className='dash-col-secondaire'>
                  Échangé
                </th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((c) => {
                const sansEchange = !c.volume_echange;
                return (
                  <tr key={c.societe_symbole}>
                    <td>
                      <Link className='dash-symbol' to={`/stock/${c.societe_symbole}`}>
                        {c.societe_symbole}
                      </Link>
                    </td>
                    <td>{c.cours_cloture?.toLocaleString('fr-FR') ?? '–'}</td>
                    <td>
                      <Variation v={c.variation_jour_pct} />
                      {sansEchange && (
                        <span
                          className='dash-badge'
                          title='Aucun titre échangé : ce n’est pas un prix traité'
                        >
                          sans échange
                        </span>
                      )}
                    </td>
                    <td>
                      <Variation v={c.variation_annee_pct} />
                    </td>
                    <td className='dash-col-secondaire'>
                      {sansEchange ? '–' : montantCourt(c.valeur_echange)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className='dash-muted' style={{ marginBottom: 0 }}>
        Un palmarès signale ce qui a bougé, pas ce qu’il faut acheter. Consultez la fiche et les
        avis du titre avant toute décision.
      </p>
    </details>
  );
};

// ---------- écran ----------

const DashboardScreen: React.FC = () => {
  const { token } = useAuth();
  const { trackAction, status: gamificationStatus } = useGamificationContext();
  const { donnees, loading, error } = useDashboard();
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('gamification_onboarding_dismissed') !== 'true';
    } catch {
      return true;
    }
  });

  React.useEffect(() => {
    if (token) trackAction('VIEW_DASHBOARD');
  }, [token, trackAction]);

  const fermerOnboarding = () => {
    setShowOnboarding(false);
    try {
      sessionStorage.setItem('gamification_onboarding_dismissed', 'true');
    } catch {
      /* stockage indisponible : la checklist réapparaîtra, sans gravité */
    }
  };

  const suitDesTitres =
    !!donnees && (donnees.mesTitres.length > 0 || donnees.aVenir.some((e) => e.symbole));

  return (
    <SiteLayout>
      <Header icon='dashboard' title='Tableau de bord' />
      <div className='dash'>
        {loading && <Loader message='Chargement de votre tableau de bord...' />}
        {!loading && error && (
          <div className='dash-error' role='alert'>
            {error}
          </div>
        )}
        {!loading && donnees && (
          <>
            <DepuisDerniereVisite elements={donnees.nouveautes} depuis={donnees.nouveautesDepuis} />
            <div className='dash-grid-2'>
              <CartePortefeuille d={donnees} />
              <AVenir evenements={donnees.aVenir} suitDesTitres={suitDesTitres} />
            </div>
            {donnees.graphiques && (
              <div className='dash-grid-2'>
                <CartePerformance g={donnees.graphiques} />
                <CarteRepartition g={donnees.graphiques} />
              </div>
            )}
            <MesTitres lignes={donnees.mesTitres} date={donnees.marche?.date ?? null} />
            <LigneMarche d={donnees} />
            <Palmares d={donnees} />
            {donnees.historiqueIndices.length > 0 && (
              <details className='dash-card dash-fold'>
                <summary>
                  <TitreBloc icone={IconeCourbe}>Indices sur 30 jours (base 100) ▾</TitreBloc>
                </summary>
                <Suspense fallback={<p className='dash-muted'>Chargement du graphique...</p>}>
                  <IndicesChart points={donnees.historiqueIndices} />
                </Suspense>
              </details>
            )}
            <nav className='dash-quick' aria-label='Accès rapide'>
              <Link to='/cotations'>Toutes les actions</Link>
              <Link to='/data'>Terminal</Link>
              <Link to='/my-assets'>Mes actifs</Link>
              <Link to='/favorites'>Mes favoris</Link>
              <Link to='/calendar'>Calendrier</Link>
            </nav>
            {showOnboarding && gamificationStatus && (
              <OnboardingChecklist
                status={gamificationStatus}
                onDismiss={fermerOnboarding}
                compact={true}
              />
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
};

export default DashboardScreen;
